import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { formatDate } from "@angular/common";
import { ChangeDetectorRef, Component, effect, input, output, ChangeDetectionStrategy } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { AppService } from "../../app.service";
import { MetadataInfo } from "../../models/metadataInfo";
import { MoovVariable } from "../../models/moovVariable";
import { OutputFieldReference } from "../../models/outputFieldReference";
import { moveItemInFormArray } from "../../theme/utils/app-helpers";

@Component({ standalone:false,
    selector: 'app-edit-file-naming',
    templateUrl: './edit-file-naming.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./edit-file-naming.component.scss'],
  })
  export class EditFileNamingComponent {
    readonly availableMetadataInfo = input<MetadataInfo[] | undefined>(undefined);
    readonly availableParameters = input<OutputFieldReference[] | undefined>(undefined);
    readonly availableCustomerVariables = input<MoovVariable[] | undefined>(undefined);
    readonly parameters = input<any>(undefined);
 
    readonly onValueChanged = output<any>();
    readonly onCopyFromProductionRule = output<any>();

    public filenameForm:FormGroup;

    private filenameFormComponentsValueChangesSubscription?:Subscription;
    private filenameFormSameAsProductionValueChangesSubscription?:Subscription;

    constructor(private cd:ChangeDetectorRef){
        this.filenameForm = new FormGroup({
            sameAsProduction: new FormControl<boolean>(true), //only used if parameters name is "testrule"
            components: new FormArray([])
            }
        );
        effect(()=>{
            this.setParameters();
        });
    }

    ngAfterViewInit(){
        this.populateForm();
        this.cd.detectChanges();
        this.filenameFormComponentsValueChangesSubscription = this.filenameForm.controls['components'].valueChanges.subscribe(value=>this.componentsChanged());
        this.filenameFormSameAsProductionValueChangesSubscription = this.filenameForm.controls['sameAsProduction'].valueChanges.subscribe(value=>this.sameAsProductionChanged(value));
        this.onValueChanged.emit({valid:this.filenameForm.valid, value:this.formValueAsJson, formDirty:false});
    }

    ngOnDestroy(){
        if(this.filenameFormComponentsValueChangesSubscription) this.filenameFormComponentsValueChangesSubscription.unsubscribe();
        if(this.filenameFormSameAsProductionValueChangesSubscription) this.filenameFormSameAsProductionValueChangesSubscription.unsubscribe();
    }

    private setParameters(){
        if(this.parameters()?.name==="productionrule") {
            this.filenameForm.controls['components'].setValidators(Validators.required);
            this.filenameForm.controls['components'].updateValueAndValidity();
        }
        else if(this.parameters()?.name==="testrule"){
            this.filenameForm.controls['sameAsProduction'].setValidators(Validators.required);
        }
    }

    get fileNamingComponentTypes(){
        return AppService.fileNamingComponentTypeArray;
    }

    get fileNamingDateTimePatterns(){
        return AppService.fileNamingDateTimePatternArray;
    }

    get formArr() {
        return this.filenameForm.get("components") as FormArray;
    }

    formGroup(index: number): FormGroup {
        return this.formArr.at(index) as FormGroup;
    }

    get parameterName(){
        return this.parameters()?.name;
    }

    get isTestRule(){
        return this.parameters()?.name=="testrule";
    }

    get sameAsProduction(){
        return this.filenameForm.get('sameAsProduction')!.value;
    }

    get formValueAsJson(){
        let componentArray:any[]=[];
        let index:number=0;
        const components = this.filenameForm.get('components') as FormArray;
        components?.value.forEach((c:any)=>{
            if(c.type?.id==4){
                //outputparameter is not a string but an object
                componentArray.push({index:index,type:c.type?.id,value:JSON.stringify(c.value)});
            }
            else{
                componentArray.push({index:index,type:c.type?.id,value:c.value?.trim()});
            }

            index++;
        });
        return JSON.stringify(componentArray);
    }

    private componentsChanged(){
        this.filenameForm.updateValueAndValidity();
        this.onValueChanged.emit({valid:this.filenameForm.valid, value:this.formValueAsJson, formDirty:true});
    }

    private sameAsProductionChanged(value:boolean){
        if(this.parameters().name==="testrule") {
            if (!this.filenameForm.get('sameAsProduction')!.value) {
                this.filenameForm.controls['components'].setValidators(Validators.required);
            }
            else {
                this.deleteAllComponents();
                this.filenameForm.controls['components'].clearValidators();
            }
            this.filenameForm.controls['components'].updateValueAndValidity({onlySelf:false,emitEvent:false})
        }
        this.onValueChanged.emit({valid:this.filenameForm.valid, value:this.formValueAsJson, formDirty:true});
    }

    private populateForm(){
        if(this.parameters()?.value){
            let parameterArray:{index:number,type:number,value:any}[] = JSON.parse(this.parameters().value);
            if(parameterArray && parameterArray.length>0){
                this.filenameForm.controls['sameAsProduction'].setValue(false);
                parameterArray.forEach(p=>{
                    this.addComponent(p.type,p.value);
                });
            }
        }
    }

    public updateParameterValue(value:string){
        this.deleteAllComponents();
        if(value){
            let parameterArray:{index:number,type:number,value:string}[] = JSON.parse(value);
            if(parameterArray){
                parameterArray.forEach(p=>{
                    this.addComponent(p.type,p.value);
                });
            }
        }
    }

    public onAddComponentClick(){
        this.addComponent(undefined,undefined);
    }

    public onCopyFromProductionRuleClick(){
        this.onCopyFromProductionRule.emit(this);
    }

    public drop(event: CdkDragDrop<any[]>) {
        moveItemInFormArray(this.formArr, event.previousIndex, event.currentIndex);
        this.filenameForm.markAsDirty();
        this.cd.detectChanges();
    }

    //#region components
    public createComponent(typeId:number|undefined, value:any): FormGroup {
        let fileNamingComponentType = (typeId && typeId>0)?this.fileNamingComponentTypes.find(t=>t.id==typeId):null;
        if(typeId==4){
            value=this.availableParameters()?.find(p=>p.index==JSON.parse(value).index);
        }
        else if(typeId==5){
            //check if variable still exists
            value = this.availableCustomerVariables()?.find(v=>v.name==value)?.name;
        }
        else if(typeId==6){
            //check if metadata still exists
            value = this.availableMetadataInfo()?.find(i=>i.name==value)?.name;
        }
        let fbGroup =  new FormGroup({
            type: new FormControl<any>(fileNamingComponentType, Validators.required),
            value:new FormControl<any>(value)
        });
        if(!value){
            //if no value make sure error is shown to user right away
            fbGroup.controls['value'].markAsTouched();
        }
        fbGroup.controls['type'].valueChanges.subscribe(d=>{
            fbGroup.controls['value'].setValue(null);
            if(d?.id!=2) fbGroup.controls['value'].setValidators(Validators.required);
            else fbGroup.controls['value'].clearValidators();
            fbGroup.controls['value'].updateValueAndValidity();
        });
        return fbGroup;
    }

    public addComponent(typeId:number|undefined, value:any) {
        if(this.filenameForm){
            const fa = this.filenameForm.get('components') as FormArray;
            let component=this.createComponent(typeId, value);
            fa.push(component);
        }
    }

    public deleteComponent(componentIndex:number) {
        const components = this.filenameForm.get('components') as FormArray;
        components.removeAt(componentIndex);
        components.markAsTouched();
    }

    public deleteAllComponents() {
        const components = this.filenameForm.get('components') as FormArray;
        components.clear();
    }
    //#endregion

    get namingPreview(){
        let previewName:string='';
        const components = this.filenameForm.get('components') as FormArray;
        components.value.forEach((c:any)=>{
            switch(c.type?.id){
                case 1:
                    if(c.value) previewName += formatDate(Date.now(),c.value.replaceAll('f','S'),'en-US');
                    break;
                case 2:
                    previewName += 'bec331aa15661f591bf10a709be9a710';
                    break;
                case 3:
                    if(c.value) previewName += c.value.trim();
                    break;
                case 4:
                    if(c.value) previewName += '['+c.value.name+']';
                    break;
                case 5:
                    if(c.value) previewName += '['+c.value+']';
                    break;
                case 6:
                    if(c.value) previewName += '['+c.value+']';
                    break;
                default:
                    break;
            }
        });
        return previewName;
    }
  }  